import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import EntityModal from '../components/common/EntityModal';
import ResolverReporteModal from '../components/documentos/ResolverReporteModal';
import { useToast } from '../components/contexts/ToastContext';
import { useAuth } from '../components/contexts/AuthContext';
import apiService from '../services/apiService';

/**
 * Página de Dashboard principal
 *
 * Muestra estadísticas de equipos, mantenimientos próximos,
 * documentos por vencer y distribución por tipo de mantenimiento.
 *
 * @returns {JSX.Element}
 */
function Home() {
  const [equipoStats, setEquipoStats] = useState(null);
  const [ordenTrabajoStats, setOrdenTrabajoStats] = useState(null);
  const [proximasOrdenes, setProximasOrdenes] = useState([]);
  const [documentosStats, setDocumentosStats] = useState(null);
  const [documentosProximos, setDocumentosProximos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para modal de documentos
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [docLoading, setDocLoading] = useState(false);

  // Estados para reportes de documentos
  const [reportesStats, setReportesStats] = useState({ pendientes: 0 });
  const [reportesPendientes, setReportesPendientes] = useState([]);
  const [resolverModalOpen, setResolverModalOpen] = useState(false);
  const [reporteToResolve, setReporteToResolve] = useState(null);
  const [resolverLoading, setResolverLoading] = useState(false);

  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canSeeAllOTs = hasPermission('ordenes_trabajo.read_all');
  const canSeeEquipos = hasPermission('equipos.read');
  const canSeeDocumentos = hasPermission('documentos_equipo.read');
  const canSeeReportes = hasPermission('reportes_documento.read');

  useEffect(() => {
    /**
     * Carga datos iniciales del dashboard según permisos del usuario
     */
    const fetchData = async () => {
      try {
        const noOp = Promise.resolve({ success: false });
        const [equiposRes, mantRes, proximosRes, docsStatsRes, docsProximosRes, reportesStatsRes, reportesPendientesRes] = await Promise.all([
          canSeeEquipos    ? apiService.getEquipoStats()                      : noOp,
          canSeeAllOTs     ? apiService.getOrdenTrabajoStats()                : noOp,
          canSeeAllOTs     ? apiService.getOrdenTrabajoProximas()             : noOp,
          canSeeDocumentos ? apiService.getDocumentosVencimientosStats()      : noOp,
          canSeeDocumentos ? apiService.getDocumentosVencimientosProximos(30) : noOp,
          canSeeReportes   ? apiService.getReportesStats()                    : noOp,
          canSeeReportes   ? apiService.getReportesPendientes({ limit: 5 })  : noOp
        ]);

        if (equiposRes.success) setEquipoStats(equiposRes.data);
        if (mantRes.success) setOrdenTrabajoStats(mantRes.data);
        if (proximosRes.success) setProximasOrdenes(proximosRes.data);
        if (docsStatsRes.success) setDocumentosStats(docsStatsRes.data);
        if (docsProximosRes.success) setDocumentosProximos(docsProximosRes.data || []);
        if (reportesStatsRes.success) setReportesStats(reportesStatsRes.data || { pendientes: 0 });
        if (reportesPendientesRes.success) setReportesPendientes(reportesPendientesRes.data || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        showToast('Error al cargar dashboard', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  /**
   * Recarga estadísticas de reportes
   */
  const fetchReportesStats = async () => {
    try {
      const response = await apiService.getReportesStats();
      if (response.success) {
        setReportesStats(response.data || { pendientes: 0 });
      }
    } catch (error) {
      console.error('Error recargando stats de reportes:', error);
      showToast('Error al actualizar estadísticas', 'error');
    }
  };

  /**
   * Recarga lista de reportes pendientes
   */
  const fetchReportesPendientes = async () => {
    try {
      const response = await apiService.getReportesPendientes({ limit: 5 });
      if (response.success) {
        setReportesPendientes(response.data || []);
      }
    } catch (error) {
      console.error('Error recargando reportes pendientes:', error);
      showToast('Error al cargar reportes', 'error');
    }
  };

  /**
   * Abre el modal para resolver/descartar un reporte
   * @param {Object} reporte - Reporte a gestionar
   */
  const handleResolverClick = (reporte) => {
    setReporteToResolve(reporte);
    setResolverModalOpen(true);
  };

  /**
   * Cierra un reporte (resolver o descartar)
   * @param {Object} params - Datos de cierre del reporte
   * @param {File} params.evidencia - Imagen de evidencia (JPG/PNG)
   * @param {string} params.notas - Notas opcionales de cierre
   * @param {string} accion - Acción a realizar: 'resuelto' o 'descartado'
   */
  const handleCerrarReporte = async ({ evidencia, notas }, accion) => {
    setResolverLoading(true);
    try {
      const response = await apiService.cerrarReporte(reporteToResolve.id, accion, evidencia, notas);
      if (response.success) {
        const mensaje = accion === 'resuelto' ? 'Reporte resuelto exitosamente' : 'Reporte descartado';
        showToast(mensaje, 'success');
        setResolverModalOpen(false);
        setReporteToResolve(null);
        // Recargar datos
        fetchReportesPendientes();
        fetchReportesStats();
      } else {
        showToast(response.message || 'Error al cerrar reporte', 'error');
      }
    } catch (error) {
      showToast('Error al cerrar reporte', 'error');
    } finally {
      setResolverLoading(false);
    }
  };

  /**
   * Resuelve un reporte marcándolo como solucionado
   * @param {Object} params - Datos de cierre
   * @param {File} params.evidencia - Imagen de evidencia
   * @param {string} params.notas - Notas de resolución
   */
  const handleResolver = (params) => handleCerrarReporte(params, 'resuelto');

  /**
   * Descarta un reporte marcándolo como no procedente
   * @param {Object} params - Datos de cierre
   * @param {File} params.evidencia - Imagen de evidencia
   * @param {string} params.notas - Notas de descarte
   */
  const handleDescartar = (params) => handleCerrarReporte(params, 'descartado');

  /**
   * Genera configuración de campos para el modal de actualización de documentos
   * @returns {Array<Object>} Configuración de campos para EntityModal
   */
  const getDocumentoFields = () => [
    {
      name: 'fecha_vencimiento',
      label: 'Fecha de Vencimiento',
      type: 'date',
      required: true
    },
    {
      name: 'archivo',
      label: 'Archivo',
      type: 'file',
      required: false,
      accept: '.pdf,.jpg,.jpeg,.png',
      hint: 'PDF, JPG, PNG (máx 5MB) - Opcional si solo actualiza fecha',
      fileNameField: 'archivo_nombre'
    }
  ];

  /**
   * Guarda documento actualizado y recarga lista de próximos a vencer
   * @param {FormData} data - Datos del formulario con archivo y fecha
   * @returns {Promise<void>}
   */
  const handleSaveDoc = async (data) => {
    setDocLoading(true);
    try {
      const response = await apiService.updateDocumentoEquipo(editingDoc.id, data);
      if (response.success) {
        showToast('Documento actualizado', 'success');
        // Recargar documentos próximos
        const docsRes = await apiService.getDocumentosVencimientosProximos(30);
        if (docsRes.success) {
          setDocumentosProximos(docsRes.data || []);
        }
        setUploadModalOpen(false);
        setEditingDoc(null);
      } else {
        showToast(response.message || 'Error al actualizar', 'error');
      }
    } catch (error) {
      showToast('Error al actualizar documento', 'error');
    } finally {
      setDocLoading(false);
    }
  };

  /**
   * Obtiene clase CSS de badge según prioridad de mantenimiento
   * @param {string} prioridad - Nivel de prioridad (critica, alta, media, baja)
   * @returns {string} Clase CSS del badge
   */
  const getPrioridadClass = (prioridad) => {
    const classes = {
      'critica': 'badge-danger',
      'alta': 'badge-warning',
      'media': 'badge-info',
      'baja': 'badge-secondary'
    };
    return classes[prioridad] || 'badge-secondary';
  };

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio' }
        ]} />

        {loading ? (
          <div className="text-center padding-lg">
            <i className="fas fa-spinner fa-spin fa-2x"></i>
            <p>Cargando datos...</p>
          </div>
        ) : (
          <>
            <div className="stats-grid">
              {canSeeEquipos && (
                <>
                  <div className="card stat-card">
                    <div className="card-content">
                      <div className="stat-icon stat-icon--primary">
                        <i className="fas fa-cogs"></i>
                      </div>
                      <div className="stat-details">
                        <h3>{equipoStats?.total || 0}</h3>
                        <p>Total Equipos</p>
                      </div>
                    </div>
                  </div>

                  <div className="card stat-card">
                    <div className="card-content">
                      <div className="stat-icon stat-icon--success">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <div className="stat-details">
                        <h3>{equipoStats?.operativo || 0}</h3>
                        <p>Operativos</p>
                      </div>
                    </div>
                  </div>

                  <div className="card stat-card">
                    <div className="card-content">
                      <div className="stat-icon stat-icon--warning">
                        <i className="fas fa-tools"></i>
                      </div>
                      <div className="stat-details">
                        <h3>{equipoStats?.en_mantenimiento || 0}</h3>
                        <p>En Mantenimiento</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {canSeeAllOTs && (
                <div className="card stat-card">
                  <div className="card-content">
                    <div className="stat-icon stat-icon--info">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div className="stat-details">
                      <h3>{ordenTrabajoStats?.vencidos || 0}</h3>
                      <p>OTs Vencidas</p>
                    </div>
                  </div>
                </div>
              )}

              {canSeeDocumentos && (
                <div className="card stat-card">
                  <div className="card-content">
                    <div className="stat-icon stat-icon--danger">
                      <i className="fas fa-file-excel"></i>
                    </div>
                    <div className="stat-details">
                      <h3>{documentosStats?.vencidos || 0}</h3>
                      <p>Documentos Vencidos</p>
                    </div>
                  </div>
                </div>
              )}

              {canSeeReportes && (
                <div className="card stat-card">
                  <div className="card-content">
                    <div className="stat-icon stat-icon--warning">
                      <i className="fas fa-flag"></i>
                    </div>
                    <div className="stat-details">
                      <h3>{reportesStats.pendientes || 0}</h3>
                      <p>Reportes Pendientes</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {canSeeAllOTs && (
              <>
                {/* Próximas Órdenes de Trabajo */}
                <div className="card margin-top-lg">
                  <div className="card-header">
                    <h3><i className="fas fa-calendar-alt"></i> Próximas Órdenes de Trabajo (7 dias)</h3>
                  </div>
                  <div>
                    {proximasOrdenes.length === 0 ? (
                      <p className="text-center text-muted">No hay órdenes de trabajo programadas para los próximos 7 dias</p>
                    ) : (
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Equipo</th>
                              <th>Tipo</th>
                              <th>Prioridad</th>
                              <th>Descripcion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {proximasOrdenes.map(mant => (
                              <tr key={mant.id}>
                                <td>{new Date(mant.fecha_ejecucion).toLocaleDateString('es-CL')}</td>
                                <td>
                                  <strong>{mant.equipo_codigo}</strong>
                                  <br />
                                  <small className="text-muted">{mant.equipo_marca} {mant.equipo_modelo}</small>
                                </td>
                                <td>
                                  <span
                                    className="badge"
                                    style={{ backgroundColor: mant.tipo_color, color: 'white', borderColor: mant.tipo_color }}
                                  >
                                    {mant.tipo_nombre}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${getPrioridadClass(mant.prioridad)}`}>
                                    {mant.prioridad}
                                  </span>
                                </td>
                                <td>{mant.descripcion?.substring(0, 50)}...</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* OTs por Tipo */}
                {ordenTrabajoStats?.por_tipo && ordenTrabajoStats.por_tipo.length > 0 && (
                  <div className="card margin-top-lg">
                    <div className="card-header">
                      <h3><i className="fas fa-chart-pie"></i> Mantenimientos por Tipo</h3>
                    </div>
                    <div>
                      <div className="stats-grid">
                        {ordenTrabajoStats.por_tipo.map(tipo => (
                          <div key={tipo.nombre} className="card">
                            <div className="card-content text-center">
                              <div
                                className="stat-icon"
                                style={{ backgroundColor: tipo.color, marginLeft: 'auto', marginRight: 'auto' }}
                              >
                                <i className="fas fa-wrench"></i>
                              </div>
                              <h3 style={{ marginTop: 'var(--space-md)' }}>{tipo.cantidad}</h3>
                              <p>{tipo.nombre}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {canSeeDocumentos && (
              <div className="card margin-top-lg">
                <div className="card-header">
                  <h3><i className="fas fa-file-alt text-primary"></i> Documentos Próximos a Vencer (30 días)</h3>
                </div>
                <div>
                  {documentosProximos.length === 0 ? (
                    <p className="text-center text-muted padding-md">No hay documentos próximos a vencer</p>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Vencimiento</th>
                            <th>Equipo</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documentosProximos.map(doc => (
                            <tr key={doc.id}>
                              <td>{new Date(doc.fecha_vencimiento).toLocaleDateString('es-CL')}</td>
                              <td>
                                <strong>{doc.equipo.codigo}</strong>
                                <br />
                                <small className="text-muted">{doc.equipo.marca} {doc.equipo.modelo}</small>
                              </td>
                              <td>{doc.tipo_nombre}</td>
                              <td>
                                <span className={`badge ${
                                  doc.dias_restantes < 0 ? 'badge-danger' :
                                  doc.dias_restantes <= 7 ? 'badge-warning' : 'badge-info'
                                }`}>
                                  {doc.dias_restantes < 0
                                    ? `Vencido hace ${Math.abs(doc.dias_restantes)} días`
                                    : doc.dias_restantes === 0
                                      ? 'Vence hoy'
                                      : `${doc.dias_restantes} días`}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="btn btn-table-action btn-ghost-primary"
                                  onClick={() => {
                                    setEditingDoc(doc);
                                    setUploadModalOpen(true);
                                  }}
                                  title="Actualizar documento"
                                >
                                  <i className="fas fa-sync-alt"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {canSeeReportes && (
              <div className="card margin-top-lg">
                <div className="card-header">
                  <h3><i className="fas fa-flag"></i> Documentos con Problemas Reportados</h3>
                </div>
                <div>
                  {reportesPendientes.length === 0 ? (
                    <p className="text-center text-muted padding-md">No hay reportes pendientes</p>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Equipo</th>
                            <th>Tipo Documento</th>
                            <th>Problema</th>
                            <th>Reportado por</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportesPendientes.map(reporte => (
                            <tr key={reporte.id}>
                              <td><strong>{reporte.equipo_codigo}</strong></td>
                              <td>{reporte.tipo_documento_nombre}</td>
                              <td>
                                <span className="badge badge-warning">
                                  {reporte.tipo_problema.replace('_', ' ')}
                                </span>
                              </td>
                              <td><small className="text-muted">{reporte.created_by}</small></td>
                              <td>
                                <button
                                  className="btn btn-table-action btn-ghost-primary"
                                  onClick={() => handleResolverClick(reporte)}
                                  title="Gestionar reporte"
                                >
                                  <i className="fas fa-tasks"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <EntityModal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setEditingDoc(null);
        }}
        onSave={handleSaveDoc}
        title="Actualizar Documento"
        fields={getDocumentoFields()}
        initialData={editingDoc ? {
          tipo_documento_id: editingDoc.tipo_documento_id,
          fecha_vencimiento: editingDoc.fecha_vencimiento,
          archivo_nombre: editingDoc.archivo_path?.split('/').pop()
        } : null}
        loading={docLoading}
      />

      <ResolverReporteModal
        isOpen={resolverModalOpen}
        onClose={() => { setResolverModalOpen(false); setReporteToResolve(null); }}
        onResolve={handleResolver}
        onDiscard={handleDescartar}
        reporte={reporteToResolve}
        loading={resolverLoading}
      />
    </>
  );
}

export default Home;
